// AI 아바타 상담 → 무료 상담 신청 — 클라이언트/서버 공용

// 기존 무료 상담 신청 폼(HeroSection)과 같은 거래 유형 값. 값 이름과 뜻이 반대이니 주의:
// buy-plate = 주선면허를 팔고 싶음, sell-plate = 주선면허를 사고 싶음,
// buy-license = 영업용 넘버를 팔고 싶음, sell-license = 영업용 넘버를 사고 싶음
export const LEAD_TYPES = [
  { value: 'buy-license', label: '번호판 팔기', full: '영업용 넘버를 팔고 싶습니다.' },
  { value: 'sell-license', label: '번호판 사기', full: '영업용 넘버를 사고 싶습니다.' },
  { value: 'buy-plate', label: '주선면허 팔기', full: '주선면허를 팔고 싶습니다.' },
  { value: 'sell-plate', label: '주선면허 사기', full: '주선면허를 사고 싶습니다.' },
] as const;
export type LeadType = (typeof LEAD_TYPES)[number]['value'];
export const LEAD_TYPE_VALUES = LEAD_TYPES.map((t) => t.value) as readonly string[];
export const leadTypeLabel = (v: string | null | undefined) => LEAD_TYPES.find((t) => t.value === v)?.label ?? '';

// 신청서 각 유형의 자유 입력 칸 (알림톡 상세정보에 나오는 칸) — 여기에 AI 상담 내용을 담는다
export const LEAD_NOTE_FIELD: Record<LeadType, string> = {
  'buy-plate': 'extraQuestionsText',
  'sell-plate': 'extraPurchaseQuestionsText',
  'buy-license': 'specialNotes',
  'sell-license': 'paymentQuestionsText',
};

export type LeadSettings = {
  enabled: boolean;
  positive_terms: string[];
  negative_terms: string[];
  type_terms: Record<string, string[]>;
  sensitivity: 'low' | 'normal' | 'high';
  offer_on_fallback: boolean;
  offer_after_dynamic: boolean;
  max_auto_offers: number;
  offer_text: string;
  offer_yes_label: string;
  offer_no_label: string;
  open_button_label: string;
  ask_type_text: string;
  ask_name_text: string;
  ask_phone_text: string;
  ask_kakao_text: string;
  confirm_text: string;
  consent_text: string;
  done_text: string;
  decline_text: string;
  updated_at: string;
};

export const LEAD_SETTINGS_SELECT =
  'enabled, positive_terms, negative_terms, type_terms, sensitivity, offer_on_fallback, offer_after_dynamic, max_auto_offers, offer_text, offer_yes_label, offer_no_label, open_button_label, ask_type_text, ask_name_text, ask_phone_text, ask_kakao_text, confirm_text, consent_text, done_text, decline_text, updated_at';

// 관리자가 바꿀 수 있는 문구 항목 (관리자 화면/저장 검증 공용)
export const LEAD_TEXT_FIELDS = [
  ['offer_text', '권유 문구', 300],
  ['offer_yes_label', '권유 - 예 버튼', 30],
  ['offer_no_label', '권유 - 아니오 버튼', 30],
  ['open_button_label', '입력창 위 신청 버튼', 30],
  ['ask_type_text', '거래 유형 질문', 200],
  ['ask_name_text', '이름 질문', 200],
  ['ask_phone_text', '연락처 질문', 200],
  ['ask_kakao_text', '카톡 연락 질문', 200],
  ['confirm_text', '확인 문구', 200],
  ['consent_text', '개인정보 동의 문구', 300],
  ['done_text', '신청 완료 문구', 300],
  ['decline_text', '거절했을 때 문구', 200],
] as const;

// 상담창에 내려주는 값 (판단 기준 단어는 내려주지 않음)
export type LeadPublicConfig = {
  max_auto_offers: number;
  texts: Pick<
    LeadSettings,
    | 'offer_text'
    | 'offer_yes_label'
    | 'offer_no_label'
    | 'open_button_label'
    | 'ask_type_text'
    | 'ask_name_text'
    | 'ask_phone_text'
    | 'ask_kakao_text'
    | 'confirm_text'
    | 'consent_text'
    | 'done_text'
    | 'decline_text'
  >;
  offer_video_url: string | null;
  offer_thumbnail_url: string | null;
  done_video_url: string | null;
  done_thumbnail_url: string | null;
};

// 질문마다 상담창에 알려주는 판단 결과
export type LeadHint = { offer: boolean; deal_type: LeadType | null };

export function normalizePhone(v: string) {
  return String(v ?? '').replace(/\D/g, '');
}

export function isValidPhone(v: string) {
  return /^0\d{8,10}$/.test(normalizePhone(v));
}

export function formatPhone(v: string) {
  const d = normalizePhone(v);
  if (d.length === 11) return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
  if (d.length === 10) return d.startsWith('02') ? `${d.slice(0, 2)}-${d.slice(2, 6)}-${d.slice(6)}` : `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 9) return `${d.slice(0, 2)}-${d.slice(2, 5)}-${d.slice(5)}`;
  return d;
}

// 채팅 신청 값 검증 (서버에서 다시 확인)
export function parseLeadSubmit(v: unknown) {
  const b = v as { name?: unknown; phone?: unknown; transactionType?: unknown; kakaoPreferred?: unknown; consent?: unknown } | null;
  const name = String(b?.name ?? '').trim().slice(0, 30);
  const phone = normalizePhone(String(b?.phone ?? ''));
  const transactionType = String(b?.transactionType ?? '');
  if (!name) return { error: '이름을 입력해주세요.' } as const;
  if (!isValidPhone(phone)) return { error: '연락처를 확인해주세요.' } as const;
  if (!LEAD_TYPE_VALUES.includes(transactionType)) return { error: '거래 유형을 선택해주세요.' } as const;
  if (b?.consent !== true) return { error: '개인정보 수집·이용에 동의해주세요.' } as const;
  return {
    name,
    phone: formatPhone(phone),
    transactionType: transactionType as LeadType,
    kakaoPreferred: b?.kakaoPreferred === true,
  } as const;
}
